import React, { useState } from 'react';
import { motion } from 'framer-motion';
import '../styles/services.css';

// SVG Imports
import Webdev from "../images/Webdev.svg";
import Appdev from "../images/Appdev.svg";
import AI from "../images/AI.svg";
import Web3 from "../images/Web3.0.svg";
import UIUX from "../images/UX.svg";
import SaaS from "../images/SAASdev.svg";
import SoftConsult from "../images/Soft Consult.svg";
import Blockchain from "../images/blockchain.svg";
import Cloud from "../images/cloud.svg";
import CMS from "../images/CMS.svg";
import DevOps from "../images/devops.svg";
import GameDev from "../images/game.svg";

const services = [
  {
    title: "Web Development",
    icon: Webdev,
    description: "Crafting responsive and dynamic websites tailored to your digital vision.",
    color: "from-blue-500 to-purple-600"
  },
  {
    title: "App Development",
    icon: Appdev,
    description: "Creating seamless and user-friendly mobile applications with cutting-edge technology.",
    color: "from-green-400 to-emerald-600"
  },
  {
    title: "AI Development",
    icon: AI,
    description: "Developing intelligent solutions that push the boundaries of artificial intelligence.",
    color: "from-red-500 to-orange-500"
  },
  {
    title: "Web 3.0 Development",
    icon: Web3,
    description: "Innovating blockchain-based web solutions for decentralized digital experiences.",
    color: "from-indigo-600 to-blue-700"
  },
  {
    title: "UI/UX Design",
    icon: UIUX,
    description: "Designing intuitive and engaging interfaces that transform user interactions.",
    color: "from-pink-500 to-rose-600"
  },
  {
    title: "SaaS Development",
    icon: SaaS,
    description: "Engineering scalable software platforms that grow with your business needs.",
    color: "from-teal-500 to-cyan-600"
  },
  {
    title: "Software Consultation",
    icon: SoftConsult,
    description: "Providing expert strategic insights to optimize your technological ecosystem.",
    color: "from-purple-600 to-indigo-700"
  },
  {
    title: "Cloud Services",
    icon: Cloud,
    description: "Leveraging cloud technologies to enhance scalability, flexibility, and performance of your digital infrastructure.",
    color: "from-sky-500 to-blue-600"
  },
  {
    title: "Blockchain Development",
    icon: Blockchain,
    description: "Creating secure, transparent, and decentralized solutions that revolutionize digital trust and transactions.",
    color: "from-emerald-500 to-green-600"
  },
  {
    title: "Game Development",
    icon: GameDev,
    description: "Designing immersive and engaging gaming experiences across multiple platforms and genres.",
    color: "from-red-500 to-orange-600"
  },
  {
    title: "DevOps Services",
    icon: DevOps,
    description: "Streamlining software development and IT operations to accelerate delivery and improve collaboration.",
    color: "from-purple-500 to-indigo-600"
  },
  {
    title: "Custom Software Development",
    icon: CMS,
    description: "Tailoring unique software solutions that precisely meet your specific business requirements and challenges.",
    color: "from-yellow-500 to-amber-600"
  }
];

const AnimatedCharacter = ({ char, index, baseDelay, customStyles = {}, isOrange = false }) => {
  return (
    <motion.span
      key={index}
      initial={{ 
        opacity: 0, 
        x: -50,
        rotate: -10
      }}
      whileInView={{ 
        opacity: 1, 
        x: 0,
        rotate: 0
      }}
      transition={{
        delay: baseDelay + index * 0.05,
        type: "spring",
        stiffness: 100,
        damping: 10
      }}
      viewport={{ once: false }}
      style={{
        display: 'inline-block',
        willChange: 'transform, opacity',
        margin: char === ' ' ? '0 0.25em' : '0', 
        color: isOrange ? '#FF6B35' : 'inherit',
        ...customStyles
      }}
      className={`char-${index + 1}`}
    >
      {char}
    </motion.span>
  );
};

const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div 
      className="service-card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ 
        delay: index * 0.2,
        duration: 0.6,
        type: "spring",
        stiffness: 100
      }}
      whileHover={{ scale: 1.05 }}
      viewport={{ once: false }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="service-card-content">
        <motion.div 
          className="service-icon-wrapper"
          animate={{ 
            rotate: isHovered ? [0, 5, -5, 0] : 0,
            scale: isHovered ? 1.1 : 1
          }}
        >
          <img 
            src={service.icon} 
            alt={`${service.title} Icon`} 
            className="service-icon"
          />
        </motion.div>
        
        <div className="service-details">
          <h2 className="service-title">{service.title}</h2>
          <p className="service-description">{service.description}</p>
        </div>
        
        <motion.div 
          className={`service-hover-overlay bg-gradient-to-br ${service.color}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 0.9 : 0 }}
        />
      </div>
    </motion.div>
  );
};

const ServicesPage = () => {
  return (
    <div className="services-section" id="services">
      <motion.div 
        initial={{ opacity: 0, y: -50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false }}
        className="services-header"
      >
        <div className="services-title-wrapper">
          {["OUR", "SERVICES"].map((text, index) => (
            <div key={text} className="services-title-line">
              {text.split("").map((char, charIndex) => (
                <AnimatedCharacter
                  key={`${text}-${charIndex}`}
                  char={char}
                  index={charIndex}
                  baseDelay={index * 0.2}
                  isOrange={text === "SERVICES"}
                  customStyles={{
                    fontFamily: '"Poppins", sans-serif',
                    fontWeight: 700,
                    fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </motion.div>
      
      <motion.div 
        className="services-grid"
        initial="hidden"
        animate="visible"
      >
        {services.map((service, index) => (
          <ServiceCard 
            key={index} 
            service={service} 
            index={index}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default ServicesPage;